import React, { useEffect, useState, createElement, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, ChevronDown, ChevronUp, Shield, Zap, FileText, AlertCircle, Sparkles, Clock, Leaf, Brain, Target, Lock, Smartphone, Printer, X, HelpCircle, Scale, MessageCircle } from 'lucide-react';
// Declare fbq for TypeScript
declare global {
  interface Window {
    fbq: any;
    _fbq: any;
  }
}
// Initialize Facebook Pixel
const initFacebookPixel = () => {
  if (typeof window !== 'undefined' && !window.fbq) {
    const f = window as any;
    const b = document;
    const e = 'script';
    const v = 'https://connect.facebook.net/en_US/fbevents.js';
    let n: any;
    n = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = true;
    n.version = '2.0';
    n.queue = [];
    const t = b.createElement(e) as HTMLScriptElement;
    t.async = true;
    t.src = v;
    const s = b.getElementsByTagName(e)[0];
    s?.parentNode?.insertBefore(t, s);
    window.fbq('init', '1251535546825451');
    window.fbq('track', 'PageView');
  }
};
// Track AddToCart event
const trackAddToCart = () => {
  if (typeof window !== 'undefined' && window.fbq) {
    window.fbq('track', 'AddToCart', {
      content_name: '7-Day Natural Parasite Cleanse Bundle',
      content_type: 'product',
      value: 6.39,
      currency: 'USD'
    });
  }
};
const IMAGES = {
  main: 'https://cdn.magicpatterns.com/uploads/owbCKmx99CoSLf4vBxBVVB/heylloyd-image-1769697553179.png',
  heroBundle: 'https://cdn.magicpatterns.com/uploads/oM9dKSgkoMPokibKC4r5gH/hero_bundle.png',
  ctaBundle: 'https://cdn.magicpatterns.com/uploads/vGpjbyL5gMszQAB5hCDCjL/hero_bundle.png',
  bonus1: 'https://cdn.magicpatterns.com/uploads/r6Fh49VaJVi3SN61DVaX5r/bonus_1.png',
  bonus2: 'https://cdn.magicpatterns.com/uploads/bTprpcZMDjyEtsKxjFvFNh/bonus_2.png',
  bonus3: 'https://cdn.magicpatterns.com/uploads/q3ZLAjhzXVb9wEmS9bNepo/bonus_3.png'
};
// --- Modal Component ---
const Modal = ({
  isOpen,
  onClose,
  title,
  icon: Icon,
  children
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  icon: React.ElementType;
  children: React.ReactNode;
}) => {
  if (!isOpen) return null;
  return <AnimatePresence data-id="element-1">
      {isOpen && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" onClick={onClose} data-id="element-2" />
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        duration: 0.2
      }} className="fixed inset-4 md:inset-auto md:left-1/2 md:top-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:max-w-lg md:w-full bg-white rounded-2xl shadow-2xl z-50 flex flex-col max-h-[90vh]" data-id="element-3">
            <div className="flex items-center justify-between p-4 md:p-5 border-b border-gray-100" data-id="element-4">
              <div className="flex items-center gap-3" data-id="element-5">
                <div className="w-9 h-9 bg-[#1a3a2a] rounded-lg flex items-center justify-center" data-id="element-6">
                  <Icon className="w-4 h-4 text-[#d4af37]" data-id="element-7" />
                </div>
                <h3 className="text-lg font-semibold text-[#1a3a2a]" data-id="element-8">
                  {title}
                </h3>
              </div>
              <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors" data-id="element-9">
                <X className="w-5 h-5 text-gray-500" data-id="element-10" />
              </button>
            </div>
            <div className="p-4 md:p-5 overflow-y-auto flex-1" data-id="element-11">{children}</div>
          </motion.div>
        </>}
    </AnimatePresence>;
};
const Section = ({
  children,
  className = '',
  id = ''
}: {
  children: React.ReactNode;
  className?: string;
  id?: string;
}) => <section id={id} className={`py-10 md:py-16 lg:py-20 ${className}`} data-id="element-12">
    {children}
  </section>;
const Container = ({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) => <div className={`max-w-6xl mx-auto px-4 sm:px-6 ${className}`} data-id="element-13">
    {children}
  </div>;
const Button = ({
  children,
  className = '',
  variant = 'primary',
  size = 'default',
  onClick,
  href,
  scrollTo,
  ...props
}: any) => {
  const baseStyles = 'inline-flex items-center justify-center font-semibold rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl cursor-pointer no-underline';
  const sizes = {
    sm: 'px-4 py-2.5 text-sm',
    default: 'px-5 py-3 text-sm md:px-6 md:py-3.5 md:text-base',
    lg: 'px-6 py-3.5 text-base md:px-8 md:py-4 md:text-lg'
  };
  const variants = {
    primary: 'bg-[#d4af37] hover:bg-[#c9a962] text-[#0f2a1f]',
    secondary: 'bg-[#1a3a2a] hover:bg-[#0f2a1f] text-white border border-[#d4af37]/30'
  };
  const handleClick = (e: React.MouseEvent) => {
    if (scrollTo) {
      const section = document.getElementById(scrollTo);
      if (section) {
        section.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    } else if (href) {
      // Fire AddToCart event for Shopify cart links
      if (href.includes('vitaliarutinas.com/cart')) {
        trackAddToCart();
      }
      window.location.href = href;
    } else if (onClick) {
      onClick(e);
    }
  };
  return <button className={`${baseStyles} ${sizes[size as keyof typeof sizes]} ${variants[variant as keyof typeof variants]} ${className}`} onClick={handleClick} style={{
    textDecoration: 'none'
  }} {...props} data-id="element-14">
      {children}
    </button>;
};
const FadeIn = ({
  children,
  delay = 0
}: {
  children: React.ReactNode;
  delay?: number;
}) => <motion.div initial={{
  opacity: 0,
  y: 16
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-50px'
}} transition={{
  duration: 0.5,
  delay
}} data-id="element-15">
    {children}
  </motion.div>;
const CheckItem = ({
  children
}: {
  children: React.ReactNode;
}) => <div className="flex items-start gap-2.5" data-id="element-16">
    <div className="flex-shrink-0 mt-0.5 w-5 h-5 rounded-full bg-[#d4af37]/20 flex items-center justify-center" data-id="element-17">
      <Check className="w-3 h-3 text-[#d4af37]" strokeWidth={3} data-id="element-18" />
    </div>
    <p className="text-sm md:text-base text-gray-700" data-id="element-19">{children}</p>
  </div>;
const AccordionItem = ({
  question,
  answer
}: {
  question: string;
  answer: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="border-b border-[#1a3a2a]/10 last:border-0" data-id="element-20">
      <button className="w-full py-4 md:py-5 flex items-center justify-between text-left focus:outline-none gap-4" onClick={() => setIsOpen(!isOpen)} data-id="element-21">
        <span className="text-base md:text-lg font-semibold text-[#1a3a2a]" data-id="element-22">
          {question}
        </span>
        <div className="flex-shrink-0" data-id="element-23">
          {isOpen ? <ChevronUp className="w-5 h-5 text-[#d4af37]" data-id="element-24" /> : <ChevronDown className="w-5 h-5 text-[#d4af37]" data-id="element-25" />}
        </div>
      </button>
      <AnimatePresence data-id="element-26">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="overflow-hidden" data-id="element-27">
            <p className="pb-4 md:pb-5 text-sm md:text-base text-gray-600 leading-relaxed" data-id="element-28">
              {answer}
            </p>
          </motion.div>}
      </AnimatePresence>
    </div>;
};
export function LandingPage() {
  const [activeModal, setActiveModal] = useState<'privacy' | 'terms' | 'support' | null>(null);
  // Initialize Facebook Pixel on mount
  useEffect(() => {
    initFacebookPixel();
  }, []);
  const scrollToPricing = () => {
    const pricingSection = document.getElementById('pricing-bundle');
    if (pricingSection) {
      pricingSection.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  };
  return <div className="min-h-screen bg-[#faf8f5] overflow-x-hidden" data-id="element-29">
      {/* MODALS */}
      <Modal isOpen={activeModal === 'privacy'} onClose={() => setActiveModal(null)} title="Privacy Policy" icon={Shield} data-id="element-30">
        <div className="space-y-4 text-sm text-gray-600" data-id="element-31">
          <p className="font-medium text-[#1a3a2a]" data-id="element-32">
            Last updated: January 2025
          </p>

          <div data-id="element-33">
            <h4 className="font-semibold text-[#1a3a2a] mb-2" data-id="element-34">
              Information We Collect
            </h4>
            <p data-id="element-35">
              When you purchase our ebook, we collect only the information
              necessary to process your order: your email address and payment
              information (processed securely through our payment provider).
            </p>
          </div>

          <div data-id="element-36">
            <h4 className="font-semibold text-[#1a3a2a] mb-2" data-id="element-37">
              How We Use Your Information
            </h4>
            <ul className="list-disc pl-4 space-y-1" data-id="element-38">
              <li data-id="element-39">To deliver your purchased digital products</li>
              <li data-id="element-40">To send order confirmations and download links</li>
              <li data-id="element-41">To provide customer support when requested</li>
            </ul>
          </div>

          <div data-id="element-42">
            <h4 className="font-semibold text-[#1a3a2a] mb-2" data-id="element-43">
              Data Protection
            </h4>
            <p data-id="element-44">
              We use industry-standard encryption and secure payment processing.
              We never sell, trade, or share your personal information with
              third parties for marketing purposes.
            </p>
          </div>

          <div data-id="element-45">
            <h4 className="font-semibold text-[#1a3a2a] mb-2" data-id="element-46">Your Rights</h4>
            <p data-id="element-47">
              You may request access to, correction of, or deletion of your
              personal data at any time by contacting our support team.
            </p>
          </div>

          <div data-id="element-48">
            <h4 className="font-semibold text-[#1a3a2a] mb-2" data-id="element-49">Cookies</h4>
            <p data-id="element-50">
              We use essential cookies to ensure our website functions properly.
              No tracking cookies are used for advertising purposes.
            </p>
          </div>
        </div>
      </Modal>

      <Modal isOpen={activeModal === 'terms'} onClose={() => setActiveModal(null)} title="Terms of Service" icon={Scale} data-id="element-51">
        <div className="space-y-4 text-sm text-gray-600" data-id="element-52">
          <p className="font-medium text-[#1a3a2a]" data-id="element-53">
            Last updated: January 2025
          </p>

          <div data-id="element-54">
            <h4 className="font-semibold text-[#1a3a2a] mb-2" data-id="element-55">
              Digital Product Purchase
            </h4>
            <p data-id="element-56">
              By purchasing the 7-Day Natural Parasite Cleanse ebook and
              bonuses, you receive a non-transferable, personal license to use
              the materials for your own educational purposes.
            </p>
          </div>

          <div data-id="element-57">
            <h4 className="font-semibold text-[#1a3a2a] mb-2" data-id="element-58">Delivery</h4>
            <p data-id="element-59">
              Upon successful payment, you will receive immediate access to
              download all digital products. Download links are delivered via
              email and are available for 30 days.
            </p>
          </div>

          <div data-id="element-60">
            <h4 className="font-semibold text-[#1a3a2a] mb-2" data-id="element-61">Refund Policy</h4>
            <p data-id="element-62">
              Due to the digital nature of this product, all sales are final.
              However, if you experience technical issues accessing your
              purchase, please contact support and we will resolve the issue
              promptly.
            </p>
          </div>

          <div data-id="element-63">
            <h4 className="font-semibold text-[#1a3a2a] mb-2" data-id="element-64">
              Educational Disclaimer
            </h4>
            <p data-id="element-65">
              This ebook is for educational and informational purposes only. It
              is not intended to diagnose, treat, cure, or prevent any disease.
              Always consult with a qualified healthcare professional before
              starting any new health protocol, especially if you have existing
              medical conditions or take medications.
            </p>
          </div>

          <div data-id="element-66">
            <h4 className="font-semibold text-[#1a3a2a] mb-2" data-id="element-67">
              Intellectual Property
            </h4>
            <p data-id="element-68">
              All content, including text, graphics, and design, is protected by
              copyright. You may not reproduce, distribute, or resell any
              portion of the materials without written permission.
            </p>
          </div>
        </div>
      </Modal>

      <Modal isOpen={activeModal === 'support'} onClose={() => setActiveModal(null)} title="Support" icon={MessageCircle} data-id="element-69">
        <div className="space-y-4 text-sm text-gray-600" data-id="element-70">
          <div className="bg-[#faf8f5] p-4 rounded-xl border border-[#1a3a2a]/10" data-id="element-71">
            <h4 className="font-semibold text-[#1a3a2a] mb-2 flex items-center gap-2" data-id="element-72">
              <HelpCircle className="w-4 h-4 text-[#d4af37]" data-id="element-73" />
              How Can We Help?
            </h4>
            <p data-id="element-74">
              We're here to ensure you have a great experience with your
              purchase.
            </p>
          </div>

          <div data-id="element-75">
            <h4 className="font-semibold text-[#1a3a2a] mb-2" data-id="element-76">
              Common Questions
            </h4>
            <div className="space-y-3" data-id="element-77">
              <div className="p-3 bg-gray-50 rounded-lg" data-id="element-78">
                <p className="font-medium text-[#1a3a2a] text-xs mb-1" data-id="element-79">
                  Download Issues
                </p>
                <p className="text-xs" data-id="element-80">
                  If you haven't received your download link, please check your
                  spam folder first. Links are sent immediately after purchase.
                </p>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg" data-id="element-81">
                <p className="font-medium text-[#1a3a2a] text-xs mb-1" data-id="element-82">
                  File Format
                </p>
                <p className="text-xs" data-id="element-83">
                  All materials are delivered as PDF files, compatible with any
                  device including phones, tablets, and computers.
                </p>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg" data-id="element-84">
                <p className="font-medium text-[#1a3a2a] text-xs mb-1" data-id="element-85">
                  Printing
                </p>
                <p className="text-xs" data-id="element-86">
                  Yes! All PDFs are optimized for printing so you can have
                  physical copies of the guides and checklists.
                </p>
              </div>
            </div>
          </div>

          <div className="pt-2" data-id="element-87">
            <h4 className="font-semibold text-[#1a3a2a] mb-2" data-id="element-88">
              Still Need Help?
            </h4>
            <p className="mb-3" data-id="element-89">
              Our support team typically responds within 24 hours on business
              days.
            </p>
            <div className="flex items-center gap-2 p-3 bg-[#1a3a2a] rounded-lg text-white" data-id="element-90">
              <FileText className="w-4 h-4 text-[#d4af37]" data-id="element-91" />
              <span className="text-xs" data-id="element-92">
                Submit a request through your order confirmation email
              </span>
            </div>
          </div>
        </div>
      </Modal>

      {/* HERO */}
      <div className="relative bg-[#1a3a2a] text-white pt-8 pb-12 md:pt-12 md:pb-16 lg:pt-16 lg:pb-20 overflow-hidden" data-id="element-93">
        <div className="absolute inset-0 opacity-5 pointer-events-none" data-id="element-94">
          <svg className="h-full w-full" viewBox="0 0 100 100" preserveAspectRatio="none" data-id="element-95">
            <path d="M0 100 C 20 0 50 0 100 100 Z" fill="#d4af37" data-id="element-96" />
          </svg>
        </div>

        <Container className="relative z-10" data-id="element-97">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center" data-id="element-98">
            <FadeIn data-id="element-99">
              <div className="space-y-5 md:space-y-6" data-id="element-100">
                <div className="inline-block px-3 py-1 rounded-full bg-[#d4af37]/15 border border-[#d4af37]/40 text-[#d4af37] text-xs font-semibold tracking-wide uppercase" data-id="element-101">
                  7-Day Protocol
                </div>
                <h1 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-serif font-bold leading-tight" data-id="element-102">
                  If Your Gut Feels "Off" and You're Tired of Guessing…{' '}
                  <span className="text-[#d4af37] italic" data-id="element-103">
                    Do This 7-Day Reset.
                  </span>
                </h1>
                <p className="text-sm md:text-base lg:text-lg text-gray-300 leading-relaxed" data-id="element-104">
                  A no-fluff, food-based cleanse built around traditional
                  ingredients (garlic, oregano, ginger, papaya, pumpkin seeds) +
                  a day-by-day plan so you stop Googling symptoms and start
                  following a clear protocol.
                </p>

                <div className="space-y-2.5" data-id="element-105">
                  {['7 days. Clear rules. No overthinking.', 'Recipes + daily schedule + shopping list', 'Built to support digestion + elimination'].map((item, i) => <div key={i} className="flex items-center gap-2.5" data-id="element-106">
                      <Check className="w-4 h-4 text-[#d4af37] flex-shrink-0" strokeWidth={3} data-id="element-107" />
                      <span className="text-sm md:text-base" data-id="element-108">{item}</span>
                    </div>)}
                </div>

                <div className="pt-2" data-id="element-109">
                  <div className="flex flex-col sm:flex-row items-center gap-4" data-id="element-110">
                    <Button scrollTo="pricing-bundle" data-id="element-111">
                      Get Instant Access — $6.39
                    </Button>
                    <img src={IMAGES.ctaBundle} alt="Bundle Preview" className="w-16 h-16 object-contain hidden sm:block" data-id="element-112" />
                  </div>
                  <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-[10px] md:text-xs text-gray-400 uppercase tracking-wider" data-id="element-113">
                    <span className="flex items-center gap-1" data-id="element-114">
                      <Zap className="w-3 h-3" data-id="element-115" /> Instant Access
                    </span>
                    <span className="flex items-center gap-1" data-id="element-116">
                      <Lock className="w-3 h-3" data-id="element-117" /> Secure Checkout
                    </span>
                    <span className="flex items-center gap-1" data-id="element-118">
                      <Smartphone className="w-3 h-3" data-id="element-119" /> Mobile Ready
                    </span>
                  </div>
                </div>
              </div>
            </FadeIn>

            <FadeIn delay={0.15} data-id="element-120">
              <div className="relative mx-auto max-w-xs sm:max-w-sm lg:max-w-md" data-id="element-121">
                <div className="absolute inset-0 bg-[#d4af37] blur-[80px] opacity-15 rounded-full" data-id="element-122"></div>
                <img src={IMAGES.heroBundle} alt="7-Day Natural Parasite Cleanse Ebook Bundle" className="relative z-10 w-full drop-shadow-2xl" data-id="element-123" />
              </div>
            </FadeIn>
          </div>
        </Container>
      </div>

      {/* HOOK */}
      <Section className="bg-white" data-id="element-124">
        <Container className="max-w-2xl text-center" data-id="element-125">
          <FadeIn data-id="element-126">
            <h2 className="text-xl md:text-2xl lg:text-3xl font-serif font-bold text-[#1a3a2a] mb-5 md:mb-6" data-id="element-127">
              Let's be real.
            </h2>
            <div className="text-sm md:text-base text-gray-600 space-y-4 leading-relaxed" data-id="element-128">
              <p data-id="element-129">
                Most "cleanses" are just expensive chaos: random supplements,
                extreme rules you can't maintain, and a whole lot of "trust me"
                energy.
              </p>
              <p data-id="element-130">Then you feel worse… and you blame yourself.</p>
              <p className="font-semibold text-[#1a3a2a]" data-id="element-131">
                But the problem usually isn't your discipline.
              </p>
              <p data-id="element-132">
                It's that you were never given a plan that's simple enough to
                follow and smart enough to support what matters most: hydration,
                fiber, digestion, and daily elimination.
              </p>
              <p className="text-lg md:text-xl font-serif italic text-[#d4af37] font-semibold pt-2" data-id="element-133">
                That's what this is.
              </p>
            </div>
          </FadeIn>
        </Container>
      </Section>

      {/* PAIN POINTS */}
      <Section className="bg-[#faf8f5]" data-id="element-134">
        <Container data-id="element-135">
          <div className="bg-white rounded-2xl p-5 md:p-8 lg:p-10 shadow-lg border border-[#1a3a2a]/5" data-id="element-136">
            <div className="text-center mb-6 md:mb-8" data-id="element-137">
              <span className="text-[#d4af37] font-semibold tracking-wider uppercase text-xs" data-id="element-138">
                The Pain
              </span>
              <h2 className="text-xl md:text-2xl lg:text-3xl font-serif font-bold text-[#1a3a2a] mt-1" data-id="element-139">
                Do any of these hit a little too close to home?
              </h2>
            </div>

            <div className="grid sm:grid-cols-2 gap-3 md:gap-4 max-w-3xl mx-auto" data-id="element-140">
              {['Your stomach bloats like a balloon after meals', 'Your cravings (especially sugar) feel louder than you', 'Your energy is weird: tired, foggy, unmotivated', 'You\'re doing "healthy things"… but your body isn\'t cooperating', 'You want something natural—without turning your kitchen into a supplement store'].map((pain, i) => <FadeIn key={i} delay={i * 0.05} data-id="element-141">
                  <div className="flex items-start gap-3 p-3 md:p-4 rounded-xl bg-red-50/70 border border-red-100" data-id="element-142">
                    <AlertCircle className="w-4 h-4 md:w-5 md:h-5 text-red-400 flex-shrink-0 mt-0.5" data-id="element-143" />
                    <p className="text-xs md:text-sm text-gray-700" data-id="element-144">{pain}</p>
                  </div>
                </FadeIn>)}
            </div>

            <div className="mt-6 md:mt-8 text-center" data-id="element-145">
              <p className="text-sm md:text-base text-[#1a3a2a]" data-id="element-146">
                If you're nodding… you don't need another random protocol.
                <br data-id="element-147" />
                <span className="font-semibold border-b-2 border-[#d4af37]/50" data-id="element-148">
                  You need a short, structured reset you can actually execute.
                </span>
              </p>
            </div>
          </div>
        </Container>
      </Section>

      {/* TRUTH */}
      <Section className="bg-[#1a3a2a] text-white relative overflow-hidden" data-id="element-149">
        <div className="absolute top-0 right-0 w-48 h-48 bg-[#d4af37] rounded-full filter blur-[100px] opacity-10" data-id="element-150"></div>
        <Container data-id="element-151">
          <div className="max-w-3xl mx-auto text-center" data-id="element-152">
            <FadeIn data-id="element-153">
              <h2 className="text-xl md:text-2xl lg:text-3xl font-serif font-bold mb-5 md:mb-6" data-id="element-154">
                The #1 reason people fail "cleanses"
              </h2>
              <div className="bg-white/5 backdrop-blur-sm rounded-xl p-5 md:p-8 border border-white/10" data-id="element-155">
                <p className="text-base md:text-xl lg:text-2xl font-light mb-4" data-id="element-156">
                  They try to go straight to{' '}
                  <span className="text-[#d4af37] font-semibold" data-id="element-157">
                    "destroy mode"
                  </span>
                  … without supporting elimination.
                </p>
                <p className="text-sm md:text-base text-gray-300 mb-5" data-id="element-158">
                  So everything backs up. You feel gross. You panic. You quit.
                </p>
                <div className="h-px w-16 bg-[#d4af37]/50 mx-auto mb-5" data-id="element-159"></div>
                <p className="text-sm md:text-base font-medium mb-4" data-id="element-160">
                  This ebook is designed to keep it doable:
                </p>
                <div className="grid grid-cols-3 gap-2 md:gap-4" data-id="element-161">
                  {[{
                  icon: Leaf,
                  text: 'Gentle, food-based'
                }, {
                  icon: Target,
                  text: 'Clear daily steps'
                }, {
                  icon: Shield,
                  text: 'Built-in adjustments'
                }].map((item, i) => <div key={i} className="bg-[#0f2a1f] p-3 md:p-4 rounded-lg border border-[#d4af37]/20" data-id="element-162">
                      <item.icon className="w-5 h-5 text-[#d4af37] mx-auto mb-1.5" data-id="element-163" />
                      <p className="text-[10px] md:text-xs font-medium" data-id="element-164">
                        {item.text}
                      </p>
                    </div>)}
                </div>
              </div>
            </FadeIn>
          </div>
        </Container>
      </Section>

      {/* PRODUCT */}
      <Section data-id="element-165">
        <Container data-id="element-166">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center" data-id="element-167">
            <FadeIn data-id="element-168">
              <div className="relative max-w-sm mx-auto lg:max-w-none" data-id="element-169">
                <img src={IMAGES.main} alt="Product Mockup" className="relative z-10 w-full rounded-lg shadow-xl" data-id="element-170" />
              </div>
            </FadeIn>

            <FadeIn delay={0.1} data-id="element-171">
              <div data-id="element-172">
                <span className="text-[#d4af37] font-semibold tracking-wider uppercase text-xs" data-id="element-173">
                  Introducing The Ebook
                </span>
                <h2 className="text-2xl md:text-3xl lg:text-4xl font-serif font-bold text-[#1a3a2a] mt-1 mb-3 md:mb-4" data-id="element-174">
                  7-Day Natural Parasite Cleanse
                </h2>
                <p className="text-sm md:text-base text-gray-600 mb-5 md:mb-6" data-id="element-175">
                  How to Support Your Body in Removing Parasites Naturally (With
                  Recipes + Daily Plan)
                </p>

                <div className="space-y-2.5 md:space-y-3 mb-6 md:mb-8" data-id="element-176">
                  {["A prep checklist so you don't start sloppy", 'Day-by-day schedule (what to do each day)', "Recipes you'll actually make: teas, shots, smoothies", '"No sugar" rules (because cravings don\'t negotiate)', 'What to expect + how to adjust if you feel off', 'A simple post-7-day maintenance plan'].map((item, i) => <CheckItem key={i} data-id="element-177">{item}</CheckItem>)}
                </div>

                <Button href="https://vitaliarutinas.com/cart/44425767288911:1" data-id="element-178">
                  Get Instant Access — $6.39
                </Button>
              </div>
            </FadeIn>
          </div>
        </Container>
      </Section>

      {/* OUTCOMES */}
      <Section className="bg-white border-y border-gray-100" data-id="element-179">
        <Container data-id="element-180">
          <div className="text-center max-w-2xl mx-auto mb-8 md:mb-10" data-id="element-181">
            <h2 className="text-xl md:text-2xl lg:text-3xl font-serif font-bold text-[#1a3a2a]" data-id="element-182">
              You're not buying "information."
              <br data-id="element-183" />
              <span className="text-[#d4af37]" data-id="element-184">
                You're buying relief from guessing.
              </span>
            </h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4" data-id="element-185">
            {[{
            icon: Sparkles,
            title: 'Less Bloated',
            desc: 'Feel lighter after meals'
          }, {
            icon: Clock,
            title: 'Better Digestion',
            desc: 'Consistent daily rhythm'
          }, {
            icon: Shield,
            title: 'Fewer Cravings',
            desc: 'Sugar loses its grip'
          }, {
            icon: Brain,
            title: 'Mental Clarity',
            desc: 'Steady focus & energy'
          }, {
            icon: Target,
            title: 'Confidence',
            desc: 'A plan you can repeat'
          }].map((item, i) => <FadeIn key={i} delay={i * 0.05} data-id="element-186">
                <div className="bg-[#faf8f5] p-4 md:p-5 rounded-xl h-full" data-id="element-187">
                  <div className="w-9 h-9 md:w-10 md:h-10 bg-[#1a3a2a] rounded-lg flex items-center justify-center mb-3" data-id="element-188">
                    <item.icon className="w-4 h-4 md:w-5 md:h-5 text-[#d4af37]" data-id="element-189" />
                  </div>
                  <h3 className="text-sm md:text-base font-semibold text-[#1a3a2a] mb-1" data-id="element-190">
                    {item.title}
                  </h3>
                  <p className="text-xs md:text-sm text-gray-500" data-id="element-191">
                    {item.desc}
                  </p>
                </div>
              </FadeIn>)}
          </div>
        </Container>
      </Section>

      {/* BONUSES */}
      <Section id="pricing-bundle" className="bg-[#1a3a2a] text-white" data-id="element-192">
        <Container data-id="element-193">
          <div className="text-center mb-8 md:mb-10" data-id="element-194">
            <span className="inline-block px-3 py-1 rounded-full bg-[#d4af37] text-[#1a3a2a] font-semibold text-xs uppercase tracking-wide mb-3" data-id="element-195">
              Free Today
            </span>
            <h2 className="text-xl md:text-2xl lg:text-3xl font-serif font-bold" data-id="element-196">
              Bonus Stack <span className="text-[#d4af37]" data-id="element-197">(Value: $47)</span>
            </h2>
            <p className="text-gray-400 mt-2 text-sm" data-id="element-198">
              Included free when you grab the ebook today.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-4 md:gap-5" data-id="element-199">
            {[{
            img: IMAGES.bonus1,
            value: '$17',
            title: 'Plan Express 7 Días',
            desc: 'Menu + Portions + Substitutions. Never get stuck on "what do I eat today?"'
          }, {
            img: IMAGES.bonus2,
            value: '$15',
            title: 'Guía Anti-Die-Off',
            desc: 'Signals, Adjustments & "Plan B". Know what\'s normal vs a red flag.'
          }, {
            img: IMAGES.bonus3,
            value: '$15',
            title: 'Kitchen Setup Anti-Sugar',
            desc: 'Hidden List + Scripts + Emergency Plan. Set up your environment in 30 min.'
          }].map((bonus, i) => <FadeIn key={i} delay={i * 0.1} data-id="element-200">
                <div className="bg-[#0f2a1f] rounded-xl overflow-hidden border border-[#d4af37]/15 h-full flex flex-col" data-id="element-201">
                  <div className="h-36 md:h-40 bg-[#1a3a2a] relative overflow-hidden" data-id="element-202">
                    <img src={bonus.img} alt={bonus.title} className="w-full h-full object-cover" data-id="element-203" />
                  </div>
                  <div className="p-4 md:p-5 flex-1 flex flex-col" data-id="element-204">
                    <div className="text-[#d4af37] font-semibold text-xs mb-1.5" data-id="element-205">
                      BONUS #{i + 1} (Value {bonus.value})
                    </div>
                    <h3 className="text-base md:text-lg font-semibold mb-2" data-id="element-206">
                      {bonus.title}
                    </h3>
                    <p className="text-gray-400 text-xs md:text-sm flex-1" data-id="element-207">
                      {bonus.desc}
                    </p>
                  </div>
                </div>
              </FadeIn>)}
          </div>

          <div className="mt-8 md:mt-10 text-center bg-[#d4af37]/10 rounded-xl p-5 md:p-6 border border-[#d4af37]/25 max-w-xl mx-auto" data-id="element-208">
            <p className="text-base md:text-lg font-serif mb-3" data-id="element-209">
              Total Value:{' '}
              <span className="line-through text-gray-500" data-id="element-210">$53.39</span>
            </p>
            <p className="text-2xl md:text-3xl font-bold text-[#d4af37] mb-4" data-id="element-211">
              Today Only: $6.39
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-212">
              <Button href="https://vitaliarutinas.com/cart/44425767288911:1" className="w-full sm:w-auto" data-id="element-213">
                Get The Full Bundle Now
              </Button>
              <img src={IMAGES.ctaBundle} alt="Bundle Preview" className="w-20 h-20 object-contain hidden sm:block" data-id="element-214" />
            </div>
          </div>
        </Container>
      </Section>

      {/* TABLE */}
      <Section className="bg-white" data-id="element-215">
        <Container className="max-w-3xl" data-id="element-216">
          <h2 className="text-xl md:text-2xl font-serif font-bold text-[#1a3a2a] text-center mb-6 md:mb-8" data-id="element-217">
            What You Get (Instant Download)
          </h2>
          <div className="overflow-hidden rounded-xl border border-gray-200" data-id="element-218">
            <table className="w-full text-left" data-id="element-219">
              <thead data-id="element-220">
                <tr className="bg-gray-50 border-b border-gray-200" data-id="element-221">
                  <th className="py-3 px-4 text-xs md:text-sm font-semibold text-[#1a3a2a]" data-id="element-222">
                    Included
                  </th>
                  <th className="py-3 px-4 text-xs md:text-sm font-semibold text-[#1a3a2a]" data-id="element-223">
                    Format
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100" data-id="element-224">
                {[{
                name: 'Main Ebook (7-day plan + recipes)',
                format: 'PDF',
                icon: FileText
              }, {
                name: 'Bonus #1: Plan Express',
                format: 'PDF + Printable',
                icon: Printer
              }, {
                name: 'Bonus #2: Anti-Die-Off Guide',
                format: 'PDF + Tracker',
                icon: Shield
              }, {
                name: 'Bonus #3: Kitchen Setup',
                format: 'PDF + Scripts',
                icon: Target
              }].map((row, i) => <tr key={i} data-id="element-225">
                    <td className="py-3 px-4 text-xs md:text-sm text-gray-700 flex items-center gap-2" data-id="element-226">
                      <row.icon className="w-4 h-4 text-[#d4af37] flex-shrink-0" data-id="element-227" />
                      {row.name}
                    </td>
                    <td className="py-3 px-4 text-xs md:text-sm text-gray-500" data-id="element-228">
                      {row.format}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </Container>
      </Section>

      {/* WHO FOR */}
      <Section className="bg-[#faf8f5]" data-id="element-229">
        <Container data-id="element-230">
          <div className="grid md:grid-cols-2 gap-4 md:gap-6" data-id="element-231">
            <FadeIn data-id="element-232">
              <div className="bg-white p-5 md:p-6 rounded-xl shadow-sm border-l-4 border-[#1a3a2a]" data-id="element-233">
                <h3 className="text-base md:text-lg font-semibold text-[#1a3a2a] mb-4 flex items-center gap-2" data-id="element-234">
                  <Check className="w-5 h-5 text-green-600" data-id="element-235" />
                  This is for you if:
                </h3>
                <ul className="space-y-2.5" data-id="element-236">
                  {['You want a natural, food-first reset', 'You want structure (not vibes)', "You're ready to cut sugar for 7 days"].map((item, i) => <li key={i} className="flex items-start text-xs md:text-sm text-gray-700 gap-2" data-id="element-237">
                      <span className="w-1.5 h-1.5 bg-green-500 rounded-full mt-1.5 flex-shrink-0" data-id="element-238"></span>
                      {item}
                    </li>)}
                </ul>
              </div>
            </FadeIn>

            <FadeIn delay={0.1} data-id="element-239">
              <div className="bg-white p-5 md:p-6 rounded-xl shadow-sm border-l-4 border-red-400" data-id="element-240">
                <h3 className="text-base md:text-lg font-semibold text-[#1a3a2a] mb-4 flex items-center gap-2" data-id="element-241">
                  <AlertCircle className="w-5 h-5 text-red-400" data-id="element-242" />
                  This is NOT for you if:
                </h3>
                <ul className="space-y-2.5" data-id="element-243">
                  {['You want medical diagnosis or treatment', 'You have severe symptoms needing urgent care', "You're not willing to follow rules for 7 days"].map((item, i) => <li key={i} className="flex items-start text-xs md:text-sm text-gray-700 gap-2" data-id="element-244">
                      <span className="w-1.5 h-1.5 bg-red-400 rounded-full mt-1.5 flex-shrink-0" data-id="element-245"></span>
                      {item}
                    </li>)}
                </ul>
              </div>
            </FadeIn>
          </div>

          <p className="text-center text-gray-500 text-xs mt-5 italic max-w-xl mx-auto" data-id="element-246">
            Always consult a healthcare professional if you have medical
            conditions or take medications.
          </p>
        </Container>
      </Section>

      {/* FINAL CTA */}
      <Section className="bg-[#1a3a2a] text-white text-center" data-id="element-247">
        <Container className="max-w-2xl" data-id="element-248">
          <FadeIn data-id="element-249">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-serif font-bold mb-4 md:mb-5" data-id="element-250">
              You can keep guessing for another month…
            </h2>
            <p className="text-sm md:text-base text-gray-300 mb-6 md:mb-8" data-id="element-251">
              Or you can run a clean, simple 7-day reset—starting tomorrow
              morning.
            </p>
            <div className="bg-white/5 p-5 md:p-6 rounded-2xl border border-[#d4af37]/25 backdrop-blur-sm" data-id="element-252">
              <h3 className="text-base md:text-lg font-semibold text-[#d4af37] mb-1" data-id="element-253">
                7-Day Natural Parasite Cleanse + $47 Bonuses
              </h3>
              <div className="text-3xl md:text-4xl font-bold mb-5" data-id="element-254">
                Only $6.39
              </div>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-255">
                <Button size="lg" className="w-full sm:w-auto shadow-[0_0_20px_rgba(212,175,55,0.25)]" scrollTo="pricing-bundle" data-id="element-256">
                  Download Instantly
                </Button>
                <img src={IMAGES.ctaBundle} alt="Bundle Preview" className="w-20 h-20 object-contain hidden sm:block" data-id="element-257" />
              </div>
              <div className="mt-4 flex justify-center gap-4 md:gap-6 text-[10px] md:text-xs text-gray-400" data-id="element-258">
                <span className="flex items-center gap-1" data-id="element-259">
                  <Lock className="w-3 h-3" data-id="element-260" /> Secure
                </span>
                <span className="flex items-center gap-1" data-id="element-261">
                  <Zap className="w-3 h-3" data-id="element-262" /> Instant
                </span>
                <span className="flex items-center gap-1" data-id="element-263">
                  <FileText className="w-3 h-3" data-id="element-264" /> PDF
                </span>
              </div>
            </div>
          </FadeIn>
        </Container>
      </Section>

      {/* FAQ */}
      <Section className="bg-white" data-id="element-265">
        <Container className="max-w-2xl" data-id="element-266">
          <h2 className="text-xl md:text-2xl font-serif font-bold text-[#1a3a2a] text-center mb-6 md:mb-8" data-id="element-267">
            Frequently Asked Questions
          </h2>
          <div data-id="element-268">
            {[{
            q: 'Do I need supplements?',
            a: 'No. This is food-based. Supplements are optional.'
          }, {
            q: "What if I'm constipated?",
            a: "Don't go aggressive. Prioritize hydration + fiber + gentle support first. The guide covers this in detail."
          }, {
            q: 'Can I work out?',
            a: "Light workouts are best. Don't try to PR while you're resetting. Listen to your body."
          }, {
            q: 'Is this medical advice?',
            a: "No. Educational only. If you're concerned or on medication, talk to a professional."
          }].map((item, i) => <AccordionItem key={i} question={item.q} answer={item.a} data-id="element-269" />)}
          </div>
        </Container>
      </Section>

      {/* FOOTER */}
      <footer className="bg-[#0f2a1f] text-gray-500 py-8 text-center text-xs" data-id="element-270">
        <Container data-id="element-271">
          <p data-id="element-272">
            &copy; {new Date().getFullYear()} Natural Cleanse Protocol. All
            rights reserved.
          </p>
          <div className="mt-3 flex justify-center gap-4" data-id="element-273">
            <button onClick={() => setActiveModal('privacy')} className="hover:text-white transition-colors cursor-pointer" data-id="element-274">
              Privacy
            </button>
            <button onClick={() => setActiveModal('terms')} className="hover:text-white transition-colors cursor-pointer" data-id="element-275">
              Terms
            </button>
            <button onClick={() => setActiveModal('support')} className="hover:text-white transition-colors cursor-pointer" data-id="element-276">
              Support
            </button>
          </div>
        </Container>
      </footer>
    </div>;
}